import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  InputPropsStrict,
  TablePageChangeEvent,
  TableProps,
} from '@servicetitan/design-system';
import TechniciansModule, { useTechnicians } from '@modules/TechniciansModule';

export type { TechnicianTableRow } from '@modules/TechniciansModule';

export const useTechnicianTableState = () => {
  const { technicians, isLoading, loadFailed, hasPartialError } =
    useTechnicians();

  useEffect(() => {
    TechniciansModule.loadOnDemand();
  }, []);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState({ skip: 0, take: 4 });
  const [selectedId, setSelectedId] = useState('');

  const searchString = useMemo(() => search.trim().toLowerCase(), [search]);

  useEffect(() => {
    setPage((prev) => ({ skip: 0, take: prev.take }));
  }, [searchString]);

  const dataFiltered = useMemo(() => {
    if (!searchString) return technicians;
    return technicians.filter((t) =>
      [t.name, t.phone, t.tenantName].some((val) =>
        val?.toLowerCase().includes(searchString),
      ),
    );
  }, [searchString, technicians]);

  const dataPaginated = useMemo(
    () => dataFiltered.slice(page.skip, page.skip + page.take),
    [dataFiltered, page],
  );

  const onSearchChange: InputPropsStrict['onChange'] = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [],
  );

  const onPageChange: TableProps['onPageChange'] = useCallback(
    (event: TablePageChangeEvent) => {
      setPage(event.page);
    },
    [],
  );

  const onSelectionChange = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  return {
    data: dataPaginated,
    total: dataFiltered.length,
    loading: isLoading,
    allFailed: loadFailed,
    hasPartialError,
    search,
    page,
    selectedId,
    onSearchChange,
    onPageChange,
    onSelectionChange,
  };
};
